'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["autoComplete", "autoFocus", "ButtonContent", "buttonContentProps", "buttonSize", "buttonStyle", "buttonUse", "cache", "caretRenderer", "className", "closeOnOutsideClick", "closeOnTargetLeave", "disabled", "dropdownClassName", "dropdownContentRef", "dropdownFooter", "error", "id", "initiallyOpen", "inputRef", "isOpen", "loadOptions", "menuWidth", "minimumSearchCount", "multi", "onChange", "onPaste", "options", "placeholder", "placement", "readOnly", "resetValue", "searchClassName", "searchPlaceholder", "resetOption", "resultsClassName", "searchable", "SearchInput", "selectComponent", "selectRef", "value", "valueRenderer", "_forcePlaceholder"];
import { Component } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import ShareInput from '../../decorators/ShareInput';
import { callIfPossible } from '../../core/Functions';
import SyntheticEvent from '../../core/SyntheticEvent';
import refObject from '../../utils/propTypes/refObject';
import { getComponentPropType } from '../../utils/propTypes/componentProp';
import { OptionOrGroupType, OptionType, ValueType } from '../../types/OptionTypes';
import { PLACEMENTS } from '../../tooltip/PlacementConstants';
import UIAbstractDropdownWithSearchbox from '../../dropdown/abstract/UIAbstractDropdownWithSearchbox';
import UITypeaheadSearch from '../../typeahead/search/UITypeaheadSearch';
import UITag from '../../tag/UITag';
import { getButtonProps, getValueOption } from '../utils/SelectOptionUtils';
import { getAriaAndDataProps } from '../../utils/Props';
import styled from 'styled-components';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DISPLAY_NONE_STYLE = {
  display: 'none'
};
const buttonContentPropTypes = {
  buttonText: PropTypes.node.isRequired
};
const noop = () => {};
const getEventProps = props => {
  const eventPropKeyRegex = /^on[A-Z]/;
  return Object.keys(props).filter(key => {
    return key !== 'onClose' && key !== 'onInputChange' && key !== 'onInputKeyDown' && key !== 'onOpen' && key !== 'onInputValueChange';
  }).reduce((acc, key) => {
    if (!key) return acc;
    if (key.match(eventPropKeyRegex)) {
      acc[key] = props[key];
    }
    return acc;
  }, {});
};
const StyledUIAbstractDropdownWithSearchbox = styled(UIAbstractDropdownWithSearchbox).withConfig({
  displayName: "UISelectWithButtonAnchor__StyledUIAbstractDropdownWithSearchbox",
  componentId: "sc-65rp5v-0"
})(["&&&:hover{text-decoration:none;}"]);
export const StyledUITag = styled(UITag).withConfig({
  displayName: "UISelectWithButtonAnchor__StyledUITag",
  componentId: "sc-65rp5v-1"
})(["&&:hover{text-decoration:underline;}"]);
class UISelectWithButtonAnchor extends Component {
  constructor(...args) {
    super(...args);
    this.state = {
      inputCache: {},
      inputValue: '',
      searchCount: 0
    };
    this.focus = () => {
      const {
        onOpenChange,
        isOpen
      } = this.props;
      if (!isOpen) onOpenChange(SyntheticEvent(true));
    };
    this.selectRefCallback = ref => {
      const {
        selectRef
      } = this.props;

      // When react-select mounts, ask it how many (flattened) options it has
      this._selectRef = ref;
      const internalSelect = this.getInternalSelect();
      if (internalSelect) {
        this.setState({
          searchCount: internalSelect._flatOptions.length
        });
      }
      if (selectRef) {
        selectRef(ref);
      }
    };
    this.getInternalSelect = () => {
      // Return our react-select component instance, or null
      if (!this._selectRef) {
        return null;
      }
      if (!this._selectRef.select) {
        return this._selectRef;
      }
      // _selectRef must be an <Async> or <Creatable> wrapper
      return this._selectRef.select;
    };
    this.getInternalAsyncSelect = () => {
      // Return our react-select Async instance, or null
      if (!this._selectRef || !this._selectRef.select) {
        return null;
      }
      return this._selectRef;
    };
    this.handleInputKeyDown = evt => {
      const internalSelect = this.getInternalSelect();
      if (internalSelect) {
        internalSelect.handleKeyDown(evt);
        setTimeout(() => {
          this.forceUpdate(); // Ensure that the input renders with updated ARIA attrs on up/down
        });
      }
    };
    this.handleInputChange = evt => {
      const {
        onInputValueChange
      } = this.props;
      callIfPossible(onInputValueChange, evt);
      const internalSelect = this.getInternalSelect();
      if (internalSelect) {
        internalSelect.handleInputChange(evt);
      }
      // This setState() forces an update, ensuring that Tether is repositioned properly.
      this.setState({
        inputValue: evt.target.value
      });
    };
    this.handleInputFocus = () => {
      // React Select thinks it's closed when its input receives a blur event.
      // We don't want that, because it ignores keydown events when closed.
      const internalSelect = this.getInternalSelect();
      if (internalSelect) {
        internalSelect.setState({
          isOpen: true
        });
      }
    };
    this.handleFocusLeave = () => {
      // Temporarily disabled. This causes close-and-reopen when clicking the anchor.
      // const { onOpenChange } = this.props;
      // onOpenChange(SyntheticEvent(false));
    };
    this.handleRemoveValueClick = (optionValue, evt) => {
      const {
        onChange,
        value
      } = this.props;
      const internalSelect = this.getInternalSelect();
      if (internalSelect) {
        const internalSelectValueArray = internalSelect.getValueArray(internalSelect.props.value);
        internalSelect.setValue(internalSelectValueArray.filter(option => option.value !== optionValue));
      } else {
        // Select is unmounted (e.g. because the dropdown is closed), so we handle the change
        // ourselves. Note that the "value" prop on UISelectWithButtonAnchor is an array of options.
        const allOptions = (value || []).map(v => {
          return typeof v === 'object' ? v : this.getOptionForSingleValue(v);
        });
        const newOptions = allOptions.filter(v => v.value !== optionValue);
        onChange(newOptions);
      }
      evt.stopPropagation(); // Prevent dropdown toggle
    };
    this.hasSearchbox = ({
      loadOptions,
      minimumSearchCount,
      searchable
    }) => {
      // No search, no searchbox.
      if (!searchable) {
        return false;
      }
      // If we're in async mode, always show the searchbox. Otherwise, show it only if the number of
      // (flattened) options meets minimumSearchCount, or if a search has been entered.
      return !!loadOptions || this.state.searchCount >= minimumSearchCount || Boolean(this.state.inputValue);
    };
    this.getOptionForSingleValue = singleValue => {
      const {
        options
      } = this.props;
      // If the given "value" is actually an option, return it as-is.
      if (typeof singleValue === 'object' && singleValue.value) {
        return singleValue;
      }
      // If the given value maps to an option, return that.
      const option = getValueOption(options, singleValue);
      if (option) {
        return option;
      }
      // Otherwise, return a new option with the given value.
      return {
        text: singleValue,
        value: singleValue
      };
    };
    this.getButtonContent = ({
      disabled,
      readOnly,
      loadOptions,
      multi,
      options,
      placeholder,
      resetOption,
      resetValue,
      value,
      valueRenderer,
      _forcePlaceholder
    }) => {
      if (_forcePlaceholder) {
        return {
          valueIsValid: true,
          buttonText: placeholder
        };
      }
      if (multi) {
        const renderedMultiValues = this.renderMultiValues({
          disabled,
          readOnly,
          placeholder,
          value,
          valueRenderer
        });
        return {
          valueIsValid: true,
          buttonText: /*#__PURE__*/_jsx("span", {
            className: "private-form__selectplus--multi-values " + (Array.isArray(renderedMultiValues) ? 'has-value-tags' : 'no-value-tags'),
            children: renderedMultiValues
          })
        };
      }
      return getButtonProps({
        async: !!loadOptions,
        options,
        placeholder,
        resetOption,
        resetValue,
        value,
        valueRenderer
      });
    };
    this.renderMultiValues = ({
      disabled,
      readOnly,
      placeholder,
      value,
      valueRenderer
    }) => {
      if (!Array.isArray(value) || value.length === 0) {
        return placeholder;
      }
      return value.map(v => {
        const option = this.getOptionForSingleValue(v);
        const handleCloseClick = this.handleRemoveValueClick.bind(this, option.value);
        return /*#__PURE__*/_jsx(StyledUITag, {
          _bordered: true,
          closeable: !disabled && !readOnly && option.clearableValue !== false,
          onCloseClick: handleCloseClick,
          use: option.tagUse || 'default',
          children: valueRenderer(option)
        }, option.value);
      });
    };
    this.renderSearch = ({
      autoComplete,
      initiallyOpen,
      inputRef,
      onPaste,
      searchClassName,
      SearchInput,
      searchPlaceholder
    }, {
      hasSearchbox
    }) => {
      let ariaActiveDescendant;
      let ariaOwns;
      const internalSelect = this.getInternalSelect();
      if (internalSelect) {
        const valueArray = internalSelect.getValueArray(internalSelect.props.value);
        ariaActiveDescendant = `${internalSelect._instancePrefix}-option-${internalSelect.getFocusableOptionIndex(valueArray[0])}`;
        ariaOwns = `${internalSelect._instancePrefix}--list`;
      }
      return /*#__PURE__*/_jsx(UITypeaheadSearch, {
        "aria-activedescendant": ariaActiveDescendant,
        "aria-owns": ariaOwns,
        autoComplete: autoComplete,
        autoFocus: !initiallyOpen,
        className: classNames(searchClassName, !hasSearchbox && 'sr-only'),
        Input: SearchInput,
        inputRef: inputRef,
        onKeyDown: this.handleInputKeyDown,
        onChange: this.handleInputChange,
        onFocus: this.handleInputFocus,
        onPaste: onPaste,
        placeholder: searchPlaceholder,
        role: "combobox"
      });
    };
  }
  componentDidMount() {
    const {
      autoload,
      cache,
      isOpen,
      loadOptions,
      selectComponent
    } = this.props;

    // Mount a separate Select.Async instance to perform autoload, if needed. This is a weird hack,
    // but a necessary one since the normal Select isn't mounted until the user opens the dropdown.
    if (autoload && loadOptions && !isOpen) {
      this._autoloadSelectEl = document.createElement('div');
      const SelectComponent = selectComponent;
      ReactDOM.render( /*#__PURE__*/_jsx(SelectComponent, {
        autoload: autoload,
        cache: typeof cache === 'undefined' ? this.state.inputCache : cache,
        loadOptions: loadOptions
      }), this._autoloadSelectEl);
    }
  }
  componentWillUnmount() {
    if (this._autoloadSelectEl) {
      ReactDOM.unmountComponentAtNode(this._autoloadSelectEl);
      this._autoloadSelectEl = null;
    }
  }
  render() {
    const _this$props = this.props,
      {
        autoComplete,
        autoFocus,
        ButtonContent,
        buttonContentProps,
        buttonSize,
        buttonStyle,
        buttonUse,
        cache,
        caretRenderer,
        className,
        closeOnOutsideClick,
        closeOnTargetLeave,
        disabled,
        dropdownClassName,
        dropdownContentRef,
        dropdownFooter,
        error,
        id,
        initiallyOpen,
        inputRef,
        isOpen,
        loadOptions,
        menuWidth,
        minimumSearchCount,
        multi,
        onChange,
        onPaste,
        options,
        placeholder,
        placement,
        readOnly,
        resetValue,
        searchClassName,
        searchPlaceholder,
        resetOption,
        resultsClassName,
        searchable,
        SearchInput,
        selectComponent,
        value,
        valueRenderer,
        _forcePlaceholder
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
    const {
      buttonText,
      valueIsValid
    } = this.getButtonContent({
      disabled,
      readOnly,
      loadOptions,
      multi,
      options,
      placeholder,
      resetOption,
      resetValue,
      value,
      valueRenderer,
      _forcePlaceholder
    });
    const hasSearchbox = this.hasSearchbox({
      loadOptions,
      minimumSearchCount,
      searchable
    });
    const SelectComponent = selectComponent;
    return /*#__PURE__*/_jsxs(StyledUIAbstractDropdownWithSearchbox, Object.assign({}, getAriaAndDataProps(rest), getEventProps(rest), {
      _useNativeButton: false,
      "aria-invalid": error != null ? error : !valueIsValid,
      autoFocus: autoFocus,
      buttonText: ButtonContent == null ? buttonText : /*#__PURE__*/_jsx(ButtonContent, Object.assign({
        buttonText: buttonText
      }, buttonContentProps)),
      buttonSize: buttonSize,
      buttonUse: buttonUse,
      caretRenderer: caretRenderer,
      className: className,
      closeOnOutsideClick: closeOnOutsideClick,
      closeOnTargetLeave: closeOnTargetLeave,
      disabled: disabled,
      dropdownClassName: dropdownClassName,
      dropdownContentRef: dropdownContentRef,
      hasSearchbox: hasSearchbox,
      id: id,
      menuWidth: menuWidth,
      multiline: multi,
      onFocusLeave: this.handleFocusLeave,
      onOpenComplete: this.handleOpenComplete,
      open: isOpen,
      placement: placement,
      readOnly: readOnly,
      style: buttonStyle,
      "data-has-custom-button-content": String(!!ButtonContent),
      children: [this.renderSearch({
        autoComplete,
        initiallyOpen,
        inputRef,
        onPaste,
        searchClassName,
        SearchInput,
        searchPlaceholder
      }, {
        hasSearchbox
      }), /*#__PURE__*/_jsx("span", {
        className: classNames('private-typeahead-results', resultsClassName),
        "data-dropdown-results": true,
        "data-async-options": !!loadOptions,
        "data-test-id": "typeahead-results",
        children: /*#__PURE__*/_jsx(SelectComponent, Object.assign({}, rest, {
          autosize: false,
          cache: typeof cache === 'undefined' ? this.state.inputCache : cache,
          disabled: disabled,
          isOpen: true,
          loadOptions: loadOptions,
          multi: multi,
          onChange: onChange,
          options: options,
          readOnly: readOnly,
          ref: this.selectRefCallback,
          resetValue: resetValue,
          scrollMenuIntoView: false,
          style: DISPLAY_NONE_STYLE,
          tabIndex: "-1",
          value: value,
          valueRenderer: noop
        }))
      }), dropdownFooter]
    }));
  }
}
UISelectWithButtonAnchor.displayName = 'UISelectWithButtonAnchor';
UISelectWithButtonAnchor.propTypes = {
  autoComplete: PropTypes.string,
  autoFocus: PropTypes.bool,
  autoload: PropTypes.bool,
  ButtonContent: getComponentPropType({
    propTypes: buttonContentPropTypes
  }),
  buttonContentProps: PropTypes.object,
  buttonSize: UIAbstractDropdownWithSearchbox.propTypes.buttonSize,
  buttonStyle: PropTypes.object,
  buttonUse: UIAbstractDropdownWithSearchbox.propTypes.buttonUse,
  cache: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  caretRenderer: UIAbstractDropdownWithSearchbox.propTypes.caretRenderer,
  closeOnOutsideClick: UIAbstractDropdownWithSearchbox.propTypes.closeOnOutsideClick,
  closeOnTargetLeave: UIAbstractDropdownWithSearchbox.propTypes.closeOnTargetLeave,
  disabled: PropTypes.bool,
  dropdownClassName: PropTypes.string,
  dropdownContentRef: PropTypes.func,
  dropdownFooter: PropTypes.node,
  error: PropTypes.bool,
  initiallyOpen: PropTypes.bool.isRequired,
  inputRef: refObject.isRequired,
  menuWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf(['auto'])]),
  minimumSearchCount: PropTypes.number,
  multi: PropTypes.bool,
  isOpen: PropTypes.bool.isRequired,
  loadOptions: PropTypes.func,
  onChange: PropTypes.func,
  onInputValueChange: PropTypes.func,
  onOpenChange: PropTypes.func,
  options: PropTypes.arrayOf(OptionOrGroupType),
  placeholder: PropTypes.node,
  placement: PropTypes.oneOf(PLACEMENTS),
  readOnly: PropTypes.bool,
  resetOption: OptionType.isRequired,
  resetValue: ValueType,
  resultsClassName: PropTypes.string,
  searchable: PropTypes.bool,
  searchClassName: PropTypes.string,
  SearchInput: PropTypes.elementType,
  searchPlaceholder: PropTypes.string,
  selectComponent: PropTypes.func.isRequired,
  selectRef: PropTypes.func,
  value: PropTypes.any,
  valueRenderer: PropTypes.func,
  _forcePlaceholder: PropTypes.bool
};
UISelectWithButtonAnchor.defaultProps = Object.assign({}, UIAbstractDropdownWithSearchbox.defaultProps, {
  searchable: true
});
export default ShareInput(UISelectWithButtonAnchor);